import { useMountEffect } from './useMountEffect';
import { SITE_URL, marketId } from '../config';
import { MARKETS, alternatesFor } from '../config/markets';
import { canonicalUrlFor, routeForPath } from '../config/siteRoutes';

/**
 * Single source of truth for per-route head management in this CRA SPA.
 *
 * `scripts/prerender.js` renders each route with headless Chromium at build time
 * and snapshots the DOM, so whatever this hook injects on mount is what crawlers
 * receive as static HTML in `build/<route>/index.html`. A public route that does
 * not call `usePageMeta` therefore ships the homepage's title/description/
 * canonical baked in — which is why every public page must call it once, and why
 * adding a route to the prerender list before converting it is actively harmful.
 *
 * Mount-time set + unmount restore is correct because the values are stable for
 * the life of the mounted page; detail routes are keyed by their slug and
 * remount on change. Uses `useMountEffect` per the repo's no-`useEffect` rule.
 */

export { SITE_URL };

/** Default share/OG image — the 1200x630 Sprout brand card. */
const DEFAULT_OG_IMAGE = `${SITE_URL}/og-image.png`;

export interface PageMetaOptions {
  /** ~50-60 chars, brand-suffixed, claims-safe. */
  title: string;
  /** ~140-160 chars, keyword-led, claims-safe. */
  description: string;
  /** Absolute path for this route, e.g. "/products" or `/products/${slug}`. */
  canonicalPath: string;
  ogType?: string;
  ogImage?: string;
  twitterCard?: 'summary' | 'summary_large_image';
  /** One schema.org object or several; emitted as <script type="application/ld+json">. */
  jsonLd?: object | object[];
}

type MetaAttr = 'name' | 'property';

export function usePageMeta(options: PageMetaOptions): void {
  const {
    title,
    description,
    canonicalPath,
    ogType = 'website',
    ogImage = DEFAULT_OG_IMAGE,
    twitterCard = 'summary_large_image',
    jsonLd,
  } = options;

  useMountEffect(() => {
    // `canonicalUrlFor` returns ANOTHER market's origin for routes declared
    // `crossMarket: 'canonical'` (the shared blog), so those pages point at the
    // owning domain instead of competing with themselves across both.
    const canonicalUrl = canonicalPath.startsWith('http')
      ? canonicalPath
      : canonicalUrlFor(MARKETS, marketId, canonicalPath);

    const restorers: Array<() => void> = [];

    const previousTitle = document.title;
    document.title = title;

    const upsertMeta = (attribute: MetaAttr, key: string, content: string) => {
      const selector = `meta[${attribute}="${key}"]`;
      let element = document.head.querySelector(selector) as HTMLMetaElement | null;
      const existed = Boolean(element);

      if (!element) {
        element = document.createElement('meta');
        element.setAttribute(attribute, key);
        document.head.appendChild(element);
      }

      const previousContent = element.getAttribute('content');
      restorers.push(() => {
        if (!existed) { element?.remove(); return; }
        if (previousContent === null) { element?.removeAttribute('content'); return; }
        element?.setAttribute('content', previousContent);
      });

      element.setAttribute('content', content);
    };

    upsertMeta('name', 'description', description);
    upsertMeta('property', 'og:title', title);
    upsertMeta('property', 'og:description', description);
    upsertMeta('property', 'og:type', ogType);
    upsertMeta('property', 'og:url', canonicalUrl);
    upsertMeta('property', 'og:image', ogImage);
    upsertMeta('name', 'twitter:title', title);
    upsertMeta('name', 'twitter:description', description);
    upsertMeta('name', 'twitter:card', twitterCard);
    upsertMeta('name', 'twitter:image', ogImage);

    let canonical = document.head.querySelector('link[rel="canonical"]') as HTMLLinkElement | null;
    const canonicalExisted = Boolean(canonical);

    if (!canonical) {
      canonical = document.createElement('link');
      canonical.rel = 'canonical';
      document.head.appendChild(canonical);
    }

    const previousCanonicalHref = canonical.getAttribute('href');
    restorers.push(() => {
      if (!canonicalExisted) { canonical?.remove(); return; }
      if (previousCanonicalHref === null) { canonical?.removeAttribute('href'); return; }
      canonical?.setAttribute('href', previousCanonicalHref);
    });

    canonical.setAttribute('href', canonicalUrl);

    // Per-route hreflang. The static head in public/index.html can only declare
    // alternates for '/', so without this every route inherited the homepage's
    // set and told Google that '/pricing' had '/' as its counterpart.
    //
    // Only 'shared' routes get alternates. An 'exclusive' route has no
    // counterpart to point at, and a 'canonical' route already defers to
    // another domain — a page cannot both delegate its canonical and claim to be
    // one side of a language pair. Alternates are also suppressed entirely until
    // the markets are flipped live (see ALTERNATES_LIVE in markets.js).
    // The static head can only describe '/', so its alternates must be taken
    // DOWN on every other route rather than left to sit alongside these. Two
    // en-GB alternates pointing at different URLs is a contradiction, and the
    // stale one always names the homepage.
    const staleAlternates = Array.from(
      document.head.querySelectorAll('link[rel="alternate"][hreflang]'),
    ) as HTMLLinkElement[];
    staleAlternates.forEach((link) => {
      const anchor = link.nextSibling;
      const parent = link.parentNode;
      link.remove();
      restorers.push(() => parent?.insertBefore(link, anchor));
    });

    const route = canonicalPath.startsWith('http') ? undefined : routeForPath(canonicalPath);
    if (route?.crossMarket === 'shared') {
      alternatesFor(marketId, canonicalPath).forEach(({ hreflang, href }) => {
        const link = document.createElement('link');
        link.rel = 'alternate';
        link.hreflang = hreflang;
        link.href = href;
        link.setAttribute('data-page-alternate', hreflang);
        document.head.appendChild(link);
        restorers.push(() => link.remove());
      });
    }

    if (jsonLd) {
      const blocks = Array.isArray(jsonLd) ? jsonLd : [jsonLd];
      blocks.forEach((block, index) => {
        const script = document.createElement('script');
        script.type = 'application/ld+json';
        script.setAttribute('data-page-jsonld', String(index));
        script.textContent = JSON.stringify(block);
        document.head.appendChild(script);
        restorers.push(() => script.remove());
      });
    }

    return () => {
      document.title = previousTitle;
      restorers.reverse().forEach((restore) => restore());
    };
  });
}
